/**
 * Fetches a document's stored original and hands the preview something it can
 * render.
 *
 * The bytes cannot simply be pointed at from an `<img src>` or `<iframe src>`:
 * `GET /v1/documents/{id}/original` requires the bearer token, and the browser
 * attaches no `Authorization` header to a subresource load — the request would
 * come back 401. So the blob is fetched through the app client and turned into
 * an object URL.
 *
 * Object URLs are the whole reason this is a hook. Each one pins its blob in
 * memory until it is revoked, so a user paging through twenty scans would leak
 * twenty documents' worth of memory if the URL were created inline in a render.
 * Here exactly one URL exists at a time and the effect's cleanup revokes it — on
 * unmount, on navigating to another document, and before replacing it on reload.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/errors';

import { useDocumentsApi } from './client';
import { previewKindOf, type PreviewKind } from './preview';

export type PreviewState =
  /** This type has no in-browser rendering; nothing was fetched. */
  | { status: 'unsupported'; kind: PreviewKind }
  | { status: 'loading' }
  /** A PDF or image, ready to be pointed at by an `<iframe>` / `<img>`. */
  | { status: 'ready'; kind: 'pdf' | 'image'; url: string }
  /** Plain text, already decoded. */
  | { status: 'text'; text: string }
  /** The document is real but its stored bytes are not there (404). */
  | { status: 'missing' }
  | { status: 'error'; reload: () => void };

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * How much plain text the preview pane will show. The cap is on the decoded
 * string, not the blob: the whole file is already in memory by the time we get
 * here, so slicing the blob would save nothing — what actually hurts is handing
 * a multi-megabyte string to a `<pre>` for the browser to lay out.
 */
const MAX_TEXT_PREVIEW_CHARS = 500_000;

export function useDocumentPreview(documentId: string, mime: string, enabled: boolean): PreviewState {
  const api = useDocumentsApi();
  const kind = previewKindOf(mime);
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Held outside state so the cleanup can revoke the URL it created without
  // depending on the state it set — the two are updated in the same tick.
  const objectUrl = useRef<string | null>(null);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || kind === 'none') {
      setState({ status: 'unsupported', kind });
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    // Read through a call, not as a plain property: after one `if (signal.aborted)`
    // the compiler treats the flag as permanently false and flags every later
    // check as dead code — which is exactly backwards, since the whole point is
    // that an await in between may have flipped it.
    const cancelled = (): boolean => signal.aborted;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const blob = await api.original(documentId, signal);
        if (cancelled()) return;

        if (kind === 'text') {
          const whole = await blob.text();
          if (cancelled()) return;
          const text =
            whole.length > MAX_TEXT_PREVIEW_CHARS
              ? `${whole.slice(0, MAX_TEXT_PREVIEW_CHARS)}\n\n… preview truncated — download the original for the full text.`
              : whole;
          setState({ status: 'text', text });
          return;
        }

        const url = URL.createObjectURL(blob);
        objectUrl.current = url;
        setState({ status: 'ready', kind, url });
      } catch (error) {
        if (cancelled() || isAbort(error)) return;
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'missing' });
          return;
        }
        setState({ status: 'error', reload });
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl.current !== null) {
        URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = null;
      }
    };
  }, [api, documentId, kind, enabled, nonce, reload]);

  return state;
}
